import React from 'react';
import { Link } from 'react-router-dom';
import './css/cookies.css';

const Cookies = () => {
    const name = process.env.REACT_APP_APP_NAME || "ExamWorld";
    const contact = process.env.REACT_APP_APP_CONTACT || "+91 6387139149";
    const lastUpdated = "January 2026";

    return (
        <div className="cookiesPageWrapper">
            
            {/* Header */}
            <header className="cookiesHeaderContainer">
                <h2 className="cookiesTitle">Cookie Policy</h2>
                <p className="cookiesDate">
                    Last Updated: <strong>{lastUpdated}</strong>
                </p>
                <p className="headerSubtitle">
                    This policy explains how {name} uses cookies and similar technologies to recognize you when you visit our platform.
                </p>
            </header>

            {/* Summary Section */}
            <section className="cookiesSection summarySection">
                <div className="summaryCard infoCard">
                    <div className="summaryIcon"><i className="fa-solid fa-circle-info"></i></div>
                    <div className="summaryContent">
                        <h3>Policy Summary</h3>
                        <p>
                            This Cookie Policy explains what cookies are, how we use them, the types of cookies
                            we use, and how you can manage your cookie preferences.
                        </p>
                    </div>
                </div>
                <div className="summaryCard warningCard">
                    <div className="summaryIcon"><i className="fa-solid fa-triangle-exclamation"></i></div>
                    <div className="summaryContent">
                        <h3>Important Notice</h3>
                        <p>
                            By continuing to use {name}, you are agreeing to our use of cookies
                            as described in this policy.
                        </p>
                    </div>
                </div>
            </section>

            {/* What are Cookies */}
            <section className="cookiesSection">
                <h2>1. What are Cookies?</h2>
                <p>
                    Cookies are small text files that are placed on your computer, smartphone, or other device
                    when you visit our platform. They allow us to remember your actions and preferences over a
                    period of time, so you don't have to keep re-entering them whenever you come back or browse
                    from one page to another.
                </p>
                <div className="cookieDetailsGrid">
                    <div className="detailCard">
                        <h4>First-Party Cookies</h4>
                        <p>Set by {name} directly on your device to enhance your experience.</p>
                    </div>
                    <div className="detailCard">
                        <h4>Third-Party Cookies</h4>
                        <p>We strictly limit third-party cookies to essential payment and security gateways.</p>
                    </div>
                    <div className="detailCard">
                        <h4>Session Cookies</h4>
                        <p>Temporary cookies that expire when you close your browser.</p>
                    </div>
                    <div className="detailCard">
                        <h4>Persistent Cookies</h4>
                        <p>Cookies that remain on your device for a set period of time.</p>
                    </div>
                </div>
            </section>

            {/* How We Use Them */}
            <section className="cookiesSection">
                <h2>2. How We Use Cookies</h2>
                <div className="usageGrid">
                    <div className="usageCard">
                        <h3><i className="fa-solid fa-shield-halved textPrimary"></i> Authentication & Security</h3>
                        <ul className="cookiesList">
                            <li>Keep you logged in securely</li>
                            <li>Protect against fraudulent use</li>
                            <li>Verify your identity during mock tests</li>
                        </ul>
                    </div>
                    <div className="usageCard">
                        <h3><i className="fa-solid fa-gear textPrimary"></i> Preferences & Settings</h3>
                        <ul className="cookiesList">
                            <li>Remember your study preferences</li>
                            <li>Save language selections (if applicable)</li>
                            <li>Store theme and accessibility settings</li>
                        </ul>
                    </div>
                    <div className="usageCard">
                        <h3><i className="fa-solid fa-chart-pie textPrimary"></i> Analytics & Performance</h3>
                        <ul className="cookiesList">
                            <li>Understand usage patterns</li>
                            <li>Improve platform speed and performance</li>
                            <li>Identify popular exam categories</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Cookie Types Table */}
            <section className="cookiesSection">
                <h2>3. Cookie Categories & Duration</h2>
                <div className="tableContainer">
                    <table className="cookiesTable">
                        <thead>
                            <tr>
                                <th>Category</th>
                                <th>Purpose</th>
                                <th>Examples</th>
                                <th>Duration</th>
                                <th>Essential</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><span className="badge essentialBadge">Essential</span></td>
                                <td>Required for basic functionality</td>
                                <td>Login sessions, security tokens</td>
                                <td>Session</td>
                                <td><strong>Yes</strong></td>
                            </tr>
                            <tr>
                                <td><span className="badge functionalBadge">Functional</span></td>
                                <td>Remember preferences and settings</td>
                                <td>Theme, language, font size</td>
                                <td>30-365 days</td>
                                <td>No</td>
                            </tr>
                            <tr>
                                <td><span className="badge analyticsBadge">Analytics</span></td>
                                <td>Understand platform usage</td>
                                <td>Page visits, feature usage</td>
                                <td>1-2 years</td>
                                <td>No</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Managing Preferences */}
            <section className="cookiesSection">
                <h2>4. Managing Your Cookie Preferences</h2>
                <p>Most web browsers allow you to control cookies through their settings. You can usually find these settings in the "Options" or "Preferences" menu.</p>
                <div className="browserLinksGrid">
                    <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="browserBtn">
                        <i className="fa-brands fa-chrome"></i> Google Chrome
                    </a>
                    <a href="https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox" target="_blank" rel="noopener noreferrer" className="browserBtn">
                        <i className="fa-brands fa-firefox"></i> Mozilla Firefox
                    </a>
                    <a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="browserBtn">
                        <i className="fa-brands fa-safari"></i> Apple Safari
                    </a>
                    <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge" target="_blank" rel="noopener noreferrer" className="browserBtn">
                        <i className="fa-brands fa-edge"></i> Microsoft Edge
                    </a>
                </div>
            </section>

            {/* Contact Info */}
            <section className="cookiesSection">
                <h2>5. Contact & Questions</h2>
                <p>If you have any questions about our use of cookies or this Cookie Policy, please contact our Data Protection Officer:</p>
                <div className="contactBox">
                    <p><strong>Email:</strong> support@{name.toLowerCase()}.in</p>
                    <p><strong>Phone:</strong> {contact}</p>
                    <p><strong>Address:</strong> {process.env.REACT_APP_APP_Home}, {process.env.REACT_APP_APP_District}, {process.env.REACT_APP_APP_State}, {process.env.REACT_APP_APP_Pin}</p>
                </div>
            </section>

            {/* Footer Actions */}
            <footer className="cookiesFooter">
                <div className="actionGroup">
                    <Link to="/footer/guidelines" className="actionBtn secondaryBtn">Back to Guidelines</Link>
                    <button className="actionBtn primaryBtn" onClick={() => window.print()}><i className="fa-solid fa-print"></i> Print Policy</button>
                </div>
            </footer>
            
        </div>
    );
};

export default Cookies;